import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Pagination from "../../../../Utils";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ErrorToast } from "../../../../Utils/SweetAlert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Confirmation, SuccessToast } from "../../../../Utils/SweetAlert";
import { useMutateDeleteNotifications } from "../../hooks/useApiHook";
import "../../style.css";
import { AppNotificationIntialValues, AppNotificationSearchBarInitialValues } from "../../constant";
import { Formik } from "formik";
import SearchBox from "../../../../Components/SearchBox";
import { GetAppNotificationList } from "../../../../Api/appnotifications";
import AddButton from "../../../../Components/AddButton";
import { EditButton, DeleteButton } from "../../../../Components";
import ViewAppNotifications from "./Components/ViewAppNotification";
import { IoEye } from "react-icons/io5";
import PhaseSelect from "../../../../Components/PhaseSelect";

const AppNotificationList = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const deleteNotifications = useMutateDeleteNotifications();

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedPhase, setSelectedPhase] = useState(
      AppNotificationIntialValues.phase || ""
    );

  const {
    isFetching: isAppNotificationLoading,
    data: notificationList,
    refetch,
  } = useQuery({
    queryKey: ["get-app-notifications-list", page, limit, selectedPhase],
    queryFn: () =>
      GetAppNotificationList({
        limit: limit,
        page: page === 0 ? 1 : page + 1,
        search: formRef?.current?.values?.search,
        phase: selectedPhase || "",
      }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");

    },
  });

  const handlePageChange = async (e, pageNew) => {
    setPage(pageNew);
    refetch();
  };

  const handleLimitChange = async (event) => {
    setPage(0);
    setLimit(parseInt(event.target.value, 10));
    refetch();
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin App Notifications</title>
      </Helmet>
      <div className="page-wrapper app-notification">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">App Notifications</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-4 px-2">
                <Formik
                  initialValues={{ ...AppNotificationSearchBarInitialValues, phase: null }}
                  innerRef={formRef}
                >
                  {({ values, setFieldValue }) => {
                    const filterStyles = {
                      option: (styles, { isDisabled }) => ({
                        ...styles,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        marginTop: "0px",
                        marginBottom: "0px",
                      }),
                      control: (styles, state) => ({
                        ...styles,
                        border: "2px solid #00000061",
                        borderColor: state.isFocused ? "#AD5ADC" : "#F7F5F5",
                        boxShadow: state.isFocused ? "0 0 0 1px #AD5ADC" : "none",
                        borderRadius: "20px",
                        minHeight: 42,
                        height: 42,
                        paddingLeft: 6,
                      }),
                      valueContainer: (styles) => ({
                        ...styles,
                        paddingTop: 0,
                        paddingBottom: 0,
                        height: 38,
                      }),
                      input: (styles) => ({
                        ...styles,
                        margin: 0,
                        padding: 0,
                      }),
                      placeholder: (styles) => ({
                        ...styles,
                        color: "#6b6b6b",
                      }),
                      singleValue: (styles) => ({
                        ...styles,
                        color: "#000",
                        fontWeight: 400,
                      }),
                      menu: (styles) => ({
                        ...styles,
                        borderRadius: "20px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }),
                    };
                    return (
                      <div className="d-flex gap-2">
                        <div className="w-100">
                          <PhaseSelect
                            value={values.phase}
                            includeAllOption
                            placeholder="Filter Phase"
                            onChange={(phase) => {
                              const normalizedPhase = phase || "";
                              setFieldValue("phase", normalizedPhase);
                              setSelectedPhase(normalizedPhase);
                              setPage(0);
                            }}
                            isClearable={false}
                            selectStyles={filterStyles}
                          />
                        </div>
                        <div className="w-100">
                          <SearchBox refetch={refetch} setPage={setPage} />
                        </div>
                      </div>
                    );
                  }}
                </Formik>
              </div>
              <div>
                <AddButton
                  to="/admin/add-app-notification"
                  permission="create-app-notification"
                />
              </div>
            </div>

            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive custom-pagination table-no-padding">
                    <table
                      className="table datatable table-manager mb-0"
                      style={{ width: "100%" }}
                    >
                      <thead className="thead-light">
                        <tr>
                          <th className="col-title text-start" style={{ width: "28%" }}>Title</th>
                          <th className="col-message text-start" style={{ width: "28%" }}>Message</th>
                          <th className="col-phase text-start" style={{ width: "16%" }}>Phase</th>
                          <th className="col-day text-center" style={{ width: "12%" }}>Day</th>
                          <th className="col-action text-center" style={{ width: "16%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isAppNotificationLoading && (
                          <tr>
                            <td align="center" colSpan={4}>
                              <div className="d-flex justify-content-center align-items-center py-4">
                                <div className="spinner-border loader-theme" role="status" />
                              </div>
                            </td>
                          </tr>
                        )}
                        {!isAppNotificationLoading &&
                          notificationList !== undefined && (
                            <>
                              {notificationList?.data?.data.map((item, index) => (
                                <tr key={index} className="table disable-row">
                                  <td className="col-title text-start">
                                    <Tooltip title={item?.title} placement="top-start" arrow>
                                      <span className="category-cell-ellipsis">
                                        {item?.title || "-"}
                                      </span>
                                    </Tooltip>
                                  </td>
                                  <td className="col-message text-start">
                                    <Tooltip title={item?.message} placement="top-start" arrow>
                                      <span className="category-cell-ellipsis">
                                        {item.message?.length >= 45
                                          ? item.message.substring(0, 40) +
                                          "..."
                                          : item.message || "-"}
                                      </span>
                                    </Tooltip>
                                  </td>
                                  <td className="col-phase text-start">
                                    <span className="category-cell-ellipsis">
                                      {(item?.phase || "-")
                                        .replace(/_/g, " ")
                                        .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())}
                                    </span>
                                  </td>
                                  <td className="col-day text-center">
                                    <span className="category-cell-ellipsis">
                                      {item?.day || "-"}
                                    </span>
                                  </td>
                                  <td className="col-action text-center">
                                    <EditButton
                                      to="/admin/edit-app-notifications"
                                      state={{ id: item?._id }}
                                      permission="edit-app-notification"
                                    />
                                    <DeleteButton
                                      onDelete={() => {
                                        Confirmation("Are you sure you want to delete notification?").then(
                                          async (deleteConfirm) => {
                                            if (deleteConfirm.isConfirmed) {
                                              deleteNotifications.mutate(item?._id, {
                                                onSuccess: (response) => {
                                                  SuccessToast(response?.message || "Delete Successful.");
                                                  refetch();
                                                },
                                                onError: (error) => {
                                                  ErrorToast(error?.response?.data?.message || "Server Error.");
                                                },
                                              });
                                            }
                                          }
                                        );
                                      }}
                                      permission="delete-app-notification"
                                    />
                                    <Tooltip
                                      title="View"
                                      className="btn btn-sm btn-white text-success insight-stickey-actions-btn"
                                      onClick={() => {
                                        setDetail(item);
                                        setOpen(true);
                                      }
                                      }
                                    >
                                      <IconButton>
                                        <IoEye
                                          className="text-secondary icon-color p-0"
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </td>
                                </tr>
                              ))}
                              {notificationList?.data?.data?.length === 0 && (
                                <tr>
                                  <td className="text-center" colSpan={4}>
                                    No Records Found.
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              totalItem={notificationList?.data?.totalRecords || 0}
              itemsPerPage={limit}
              page={
                !notificationList?.data?.totalRecords ||
                  notificationList?.data?.totalRecords <= 0
                  ? 0
                  : page
              }
              handleChangePage={handlePageChange}
              handleChangeRowsPerPage={handleLimitChange}
              colSpan={4}
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="modal-wrapper">
          <ViewAppNotifications
            onClose={() => setOpen(false)}
            details={detail}
          />
        </div>
      )}
    </>
  );
};

export default AppNotificationList;
